import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { JsonApiLoaderService } from './json-api-loader.service';
import {
  filter,
  map,
  Observable,
  of,
  shareReplay,
  switchMap,
  take,
  tap,
  withLatestFrom,
} from 'rxjs';
import { ComponentStore } from '@ngrx/component-store';
import { GetEntitiesPipe, GetRequestPipe } from './+state/get-data-item.pipe';
import { JsonApiQuery } from './json-api.query';
import { DrupalIdentifier } from '@nx-icilalune/drupal-types';
import { isEqual } from '@madeinlune/ngx-operators';
import { FacadeService } from './facade.service';

export interface EntitiesState<T> {
  entities?: T[] | undefined;
  ids?: string[];
  loaded?: boolean;
  loading: boolean;
  error?: HttpErrorResponse;
  query?: JsonApiQuery;
  contextId?: string;
  offset?: number | undefined;
}

@Injectable()
export class EntitiesStore<T extends DrupalIdentifier> extends ComponentStore<
  EntitiesState<T>
> {
  readonly query$ = this.select((s) => s.query);

  readonly ids$ = this.select((s) => s.ids);

  readonly entities$ = this.select((s) => s.entities);

  readonly loading$ = this.select((s) => s.loading);

  readonly offset$ = this.select((s) => s.offset);

  #sendRequest = this.effect(
    (localQuery$: Observable<JsonApiQuery | undefined>) => {
      return localQuery$.pipe(
        filter((localQuery) => !!localQuery),
        isEqual(),
        switchMap((localQuery) => {
          if (localQuery) {
            return this.getRequest.transform(localQuery.queryId).pipe(
              take(1),
              map((globalStoreQuery) => {
                if (!globalStoreQuery) {
                  return localQuery;
                }
                const mergedQuery: JsonApiQuery = {
                  ...globalStoreQuery,
                  page: localQuery.page,
                  filters: localQuery.filters,
                };
                if (localQuery.page?.offset === 0) {
                  mergedQuery.complete = false;
                }
                return mergedQuery;
              })
            );
          }
          return of(undefined);
        }),
        tap((query) => {
          if (query) {
            const currentOffset = query?.page?.offset ?? 0;
            if (currentOffset === 0 && query?.editable) {
              this.patchState({ ids: [], entities: [], loading: true });
            } else {
              this.patchState({ loading: true });
            }
          }
        }),
        tap((query: JsonApiQuery | undefined) => {
          if (query && (!query.complete || query.page?.offset === 0)) {
            this.facade.load(query);
          }
        })
      );
    }
  );

  constructor(
    private jsonApiLoaderService: JsonApiLoaderService,
    private getEntitiesPipe: GetEntitiesPipe,
    private getRequest: GetRequestPipe,
    private facade: FacadeService
  ) {
    super({ loading: true });
    this.#sendRequest(this.query$);
    /*this.patchState(
      this.ids$.pipe(
        filter((ids) => !!ids),
        switchMap((ids) => {
          return this.getEntitiesPipe.transform<T[]>(ids).pipe(
            map((entities) => {
              console.log({ entities });
              return { entities };
            })
          );
        }),
        isEqual()
      )
    );*/
  }

  setQuery(
    query: JsonApiQuery,
    contextId?: string,
    autoLoad: boolean = false,
    reset: boolean = true
  ) {
    const queryId = contextId ? `${query.queryId}-${contextId}` : query.queryId;
    if (autoLoad) {
      const newQuery: JsonApiQuery = { ...query, queryId };
      if (reset && newQuery.page) {
        newQuery.page = { ...newQuery.page, offset: 0 };
      }
      this.patchState({ query: newQuery, contextId });
    }
    this.patchState(
      this.getRequest.transform(queryId).pipe(
        filter((storeQuery) => !storeQuery?.loading),
        switchMap((storeQuery) => {
          return this.getEntitiesPipe
            .transform<T[]>(storeQuery?.allResultIds)
            .pipe(
              map((entities) => {
                return { entities, loading: false };
              }),
              isEqual(),
              shareReplay(1),
              take(1)
            );
        })
      )
    );
  }

  load(query: JsonApiQuery) {
    this.patchState((s) => {
      return { query };
    });
  }

  loadNextPage() {
    const query = this.get((s) => s.query);
    const contextId = this.get((s) => s.contextId);
    if (query?.page) {
      const queryId = contextId
        ? `${query.queryId}-${contextId}`
        : query.queryId;
      const entities = this.get((s) => s.entities);
      const offset = entities?.length ?? 0;
      const limit = query.page?.limit;
      const newQuery: JsonApiQuery = {
        ...query,
        page: {
          limit,
          offset,
        },
      };
      this.patchState({ offset });
      this.setQuery(newQuery, contextId, true, false);
      /*this.#sendRequest(
        this.facade.getQuery$(queryId).pipe(
          map((query) => {
            if (query && query.page) {
              const entities = this.get((s) => s.entities);
              const offset = entities?.length ?? 0;
              const limit = query.page.limit;
              const newQuery = {
                ...query,
                page: {
                  limit,
                  offset,
                },
              };
              return newQuery;
            }
            return undefined;
          })
        )
      );*/
    }
  }

  #reset() {
    this.patchState((s) => {
      return { ids: [], entities: [] };
    });
  }
}
